import { ProductCategory } from "@utils/constants";
import { rewriteDeeplink } from "@utils/rewriteDeeplink";
import { Platform } from "react-native";
import { FeeResponseItem } from "./types";

type GroupItem = {
  stock_type: string;
  [key: string]: any;
};

type Group = {
  group_reference_id: string;
  group_items: GroupItem[];
  [key: string]: any;
};

export const extractStockItemIdsFromCartItems = (
  items: any[] = []
): number[] => {
  const ids = new Set<number>();

  items?.forEach((item) => {
    // Case 1: builder/grouped items
    if (Array.isArray(item?.group_items)) {
      item.group_items.forEach((groupItem: any) => {
        const id = Number(
          groupItem?.stock_item_id ?? groupItem?.item?.stock_item_id
        );
        if (Number.isFinite(id)) ids.add(id);
      });
      return;
    }

    // Case 2: regular cart items
    const id = Number(item?.stock_item_id);
    if (Number.isFinite(id)) ids.add(id);
  });

  return Array.from(ids);
};

export const RedirectFunc = (initialUrl?: string, linkTo?: any) => {
  const deepLinkNonce = String(Date.now());

  if (Platform.OS === "web") {
    const url = new URL(initialUrl);
    const path = url?.pathname;
    const params = new URLSearchParams(url.search);
    params.set("deepLinkNonce", deepLinkNonce);
    if (path !== "/webapp" && path !== "/") {
      const redirectPath = `${path}?${params.toString()}`;
      try {
        linkTo(redirectPath);
      } catch (error) {
        console.error("Failed to navigate to path:", redirectPath);
        console.error("Error details:", error);
      }
    }
  } else {
    let url = rewriteDeeplink(initialUrl);
    url = url.replace(/(^\w+:|^)\/\//, "");
    const separator = url.includes("?") ? "&" : "?";
    linkTo(`/${url}${separator}deepLinkNonce=${deepLinkNonce}`);
  }
};

export const getPrimaryLocation = (companyLocations?: []) => {
  return companyLocations?.locations?.find((loc) => loc.is_primary) || null;
};

export const withPrimaryLocation = (locations: unknown) => {
  if (!Array.isArray(locations) || locations.length === 0) return [];
  if (locations.some((location) => location?.is_primary)) return locations;
  return locations.map((location, index) => ({
    ...location,
    is_primary: index === 0,
  }));
};

export const filterLocationData = (
  customization: any,
  location: Location,
  priceMode: number,
  t?: any
) => {
  if (!customization?.shipping_location?.length || !location) return {};
  const filtered = customization.shipping_location
    .filter((config) => config.priceMode.includes(priceMode))
    .sort((a, b) => a.order - b.order);

  const result = Object.fromEntries(
    filtered
      .filter((c) => location?.[c?.key] !== undefined)
      .map((c) => {
        let value = location[c.key];

        if (c.key === "mobile_phone" && location.mobile_phone) {
          const code = location.mobile_phone_country_code
            ? `+${location.mobile_phone_country_code}`
            : "";
          value = `${code} ${location.mobile_phone}`.trim();
        }

        return [t ? t(c.label) : c.label, value];
      })
  );

  return result;
};

type CouponDiscount = {
  discount?: string | number | null;
} | null;

const parseCouponDiscount = (coupon?: CouponDiscount) => {
  const raw = String(coupon?.discount ?? "")
    .replace(/%/g, "")
    .trim();
  if (!raw) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

export const isZeroCouponDiscount = (coupon?: CouponDiscount) =>
  parseCouponDiscount(coupon) === 0;

export const couponAdjustsPrice = (coupon?: CouponDiscount) =>
  !!coupon && !isZeroCouponDiscount(coupon);

export const getCartTotalForDisplay = (
  response?: {
    total_cart_price?: number | null;
    old_total_cart_price?: number | null;
    coupon?: CouponDiscount;
  } | null
) => {
  const total = Number(response?.total_cart_price ?? 0);
  const currentTotal = Number.isFinite(total) ? total : 0;
  if (!isZeroCouponDiscount(response?.coupon)) {
    return currentTotal;
  }
  const oldTotal = Number(response?.old_total_cart_price);
  if (Number.isFinite(oldTotal) && oldTotal > 0) {
    return oldTotal;
  }
  return currentTotal;
};

export const withOriginalPricesWhenZeroDiscount = <
  T extends {
    old_total_price?: number | string | null;
    old_unit_price?: number | string | null;
    total_price?: number | string | null;
    unit_price?: number | string | null;
  }
>(
  items: T[] | undefined,
  coupon?: CouponDiscount
) => {
  if (!items || !isZeroCouponDiscount(coupon)) return items;
  return items.map((item) => {
    const oldTotal = Number(item?.old_total_price);
    const hasOldTotal = Number.isFinite(oldTotal) && oldTotal > 0;
    const hasOldUnit =
      item?.old_unit_price != null && item.old_unit_price !== "";
    if (!hasOldTotal && !hasOldUnit) return item;
    return {
      ...item,
      ...(hasOldTotal ? { total_price: oldTotal } : {}),
      ...(hasOldUnit ? { unit_price: item.old_unit_price } : {}),
    };
  });
};

export const getAvailableShippingMethods = (
  methods: FeeResponseItem[],
  price: number
) => {
  if (!methods || methods?.length === 0) return [];
  return methods
    .map((method) => {
      const { min_total, max_total, rules } = method?.fee_rules;
      const min = Number(min_total) || 0;
      const max =
        !max_total || Number(max_total) === 0 ? Infinity : Number(max_total);

      if (price >= min && price <= max) {
        return method;
      }

      if (rules && rules?.length > 0) {
        const matchedRule = rules?.find((rule) => {
          const ruleMin = Number(rule?.min_total) || 0;
          const ruleMax =
            !rule?.max_total || Number(rule?.max_total) === 0
              ? Infinity
              : Number(rule?.max_total);

          return price >= ruleMin && price <= ruleMax;
        });

        if (matchedRule) {
          return {
            ...method,
            flat_fee: matchedRule?.flat_fee,
          };
        }
      }
    })
    .filter(Boolean)
    .sort((a, b) => a!.display_order - b!.display_order);
};

export const extractJewelryWithStoneDetails = (
  itemArray: Group[] = []
): any[] => {
  return itemArray?.map((group) => {
    if (group.group_reference_id && Array.isArray(group.group_items)) {
      const jewelryItem = group.group_items.find(
        (item) =>
          item.stock_type === ProductCategory.JEWELRY ||
          item.stock_type === ProductCategory.LABGROWN_JEWELRY
      );
      const stoneItem = group.group_items.filter(
        (item) => item.stock_type !== ProductCategory.JEWELRY
      );

      if (jewelryItem) {
        return {
          ...jewelryItem?.item,
          additional_attributes: jewelryItem?.additional_attributes,
          configuration_details: jewelryItem?.configuration_details,
          product_specs: jewelryItem?.product_specs,
          product_specs_v2: jewelryItem?.product_specs_v2,
          id: jewelryItem?.id,
          actual_id: jewelryItem?.item?.id,
          total_price:
            Number(jewelryItem?.total_price) +
            Number(stoneItem[0]?.total_price) +
            Number(stoneItem?.[1]?.total_price || 0),
          stoneItemDetails: {
            ...stoneItem[0]?.item,
            id: stoneItem[0]?.id,
            actual_id: stoneItem[0]?.item?.id,
          },
          ...(stoneItem?.[1] && {
            matchingPairItemDetails: {
              ...stoneItem[1]?.item,
              id: stoneItem[1]?.id,
              actual_id: stoneItem[1]?.item?.id,
            },
          }),
          ...(!!group.group_reference_id && {
            group_reference_id: group.group_reference_id,
          }),
        };
      }
    }
    return group;
  });
};

const CLOSED_ORDER_LINE_STATUSES = new Set([
  "withdraw",
  "withdrawn",
  "accepted",
  "decline",
  "declined",
  "expired",
  "cancelled",
  "canceled",
]);

const pickOrderedItemStatus = (...candidates: any[]) => {
  const values = candidates.filter(
    (status) => status != null && String(status).trim() !== ""
  );
  return (
    values.find((status) =>
      CLOSED_ORDER_LINE_STATUSES.has(String(status).trim().toLowerCase())
    ) ?? values[0]
  );
};

export const extractJewelryWithStoneDetailsOrder = (
  itemArray: Group[] = []
): any[] => {
  return itemArray?.map((group) => {
    if (group.group_reference_id && Array.isArray(group.items)) {
      const jewelryItem = group.items.find(
        (item) => item.product_sub_type === "builder_setting"
      );
      const stoneItem = group.items.filter(
        (item) => item.product_sub_type === "builder_stone"
      );
      if (jewelryItem) {
        return {
          ...jewelryItem?.item?.item,
          stock_type: jewelryItem?.item.stock_type,
          additional_attributes: jewelryItem?.item?.additional_attributes,
          configuration_details: jewelryItem?.item?.configuration_details,
          product_specs: jewelryItem?.item?.product_specs,
          product_specs_v2: jewelryItem?.item?.product_specs_v2,
          id: jewelryItem?.id,
          status: pickOrderedItemStatus(
            jewelryItem?.status,
            jewelryItem?.item_status,
            group?.status,
            group?.item_status,
            jewelryItem?.item?.status,
            jewelryItem?.item?.item?.status
          ),
          actual_id: jewelryItem?.item?.stock_item_id,
          display_configuration: jewelryItem?.item?.item.display_configuration,
          display_configuration_v2:
            jewelryItem?.item?.item.display_configuration_v2,
          image_thumb_url: jewelryItem?.item?.item?.image_thumb_url,
          image_url: jewelryItem?.item?.item?.image_url,
          image_urls: jewelryItem?.item?.item.image_urls,
          total_price:
            Number(jewelryItem?.total_price) +
            Number(stoneItem?.[0]?.total_price) +
            Number(stoneItem?.[1]?.total_price || 0),
          stoneItemDetails: {
            ...stoneItem?.[0]?.item?.item,
            id: stoneItem?.[0]?.id,
            actual_id: stoneItem?.[0]?.item?.stock_item_id,
            stock_type: stoneItem?.[0]?.item?.stock_type,
          },
          ...(stoneItem?.[1] && {
            matchingPairItemDetails: {
              ...stoneItem[1]?.item?.item,
              id: stoneItem[1]?.id,
              actual_id: stoneItem[1]?.item?.stock_item_id,
              stock_type: stoneItem?.[1]?.item?.stock_type,
            },
          }),
          ...(!!group.group_reference_id && {
            group_reference_id: group.group_reference_id,
          }),
        };
      }
    }
    return group;
  });
};
